import { debounce, parseOptions } from './utils';

type SlugifyOptions = {
  source?: string;
  delay?: number;
};

function slugify(text: string): string {
  if (!text) return '';
  // normalize accents, then remove non-alphanum and replace with hyphens
  const normalized = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove diacritics
    .toLocaleLowerCase();
  const replaced = normalized
    .replace(/[^a-z0-9]+/g, '-') // non-alnum -> hyphen
    .replace(/^-+|-+$/g, '') // trim hyphens
    .replace(/-{2,}/g, '-'); // collapse multiples
  // Optionally limit length
  return replaced.substring(0, 200);
}

function dispatchInputEvent(el: HTMLInputElement) {
  const ev = new InputEvent('input', { bubbles: true, cancelable: true });
  el.dispatchEvent(ev);
}

function ensureInputValue(el: HTMLInputElement, value: string) {
  if (el.value !== value) {
    el.value = value;
    dispatchInputEvent(el);
  }
}

function attachToInput(el: HTMLInputElement) {
  // avoid double-binding
  if (el.dataset.slugifyBound === '1') return;
  el.dataset.slugifyBound = '1';

  const raw = el.getAttribute('data-slugify');
  const optsFromAttr = parseOptions<SlugifyOptions>(raw, { source: undefined, delay: undefined });
  const opts: SlugifyOptions = {
    ...optsFromAttr,
    delay: (optsFromAttr.delay ?? 200),
  };

  const delay = typeof opts.delay === 'number' && opts.delay > 0 ? opts.delay : 200;
  const sourceSelector = opts.source;
  if (!sourceSelector) return;

  let sourceEl = document.querySelector(sourceSelector) as HTMLElement | null;

  const tryResolveSource = () => {
    if (!sourceEl) sourceEl = document.querySelector(sourceSelector) as HTMLElement | null;
    return sourceEl;
  };

  const updateFromSource = (sEl?: HTMLElement | null) => {
    const src = sEl ?? sourceEl ?? document.querySelector(sourceSelector);
    if (!src) return;
    let text = '';
    if ('value' in (src as HTMLInputElement)) {
      text = ((src as HTMLInputElement).value || '').toString();
    } else {
      text = (src.textContent || '').toString();
    }
    ensureInputValue(el, slugify(text));
  };

  const handler = debounce(() => updateFromSource(), delay);

  const onSourceEvent = () => handler();

  const resolved = tryResolveSource();
  if (resolved) {
    // avoid attaching duplicate listeners to same source
    if (resolved.dataset.slugifySourceListener !== '1') {
      resolved.addEventListener('input', onSourceEvent as EventListener);
      resolved.addEventListener('change', onSourceEvent as EventListener);
      resolved.addEventListener('keyup', onSourceEvent as EventListener);
      resolved.dataset.slugifySourceListener = '1';
    }
    handler();
  } else {
    // observe DOM for the source selector to appear
    const observer = new MutationObserver((mutations, obs) => {
      const found = document.querySelector(sourceSelector);
      if (found) {
        obs.disconnect();
        sourceEl = found as HTMLElement;
        if (sourceEl.dataset.slugifySourceListener !== '1') {
          sourceEl.addEventListener('input', onSourceEvent as EventListener);
          sourceEl.addEventListener('change', onSourceEvent as EventListener);
          sourceEl.addEventListener('keyup', onSourceEvent as EventListener);
          sourceEl.dataset.slugifySourceListener = '1';
        }
        handler();
      }
    });
    observer.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true,
    });
    // remember observer so it can be disconnected if element removed (optional)
    el.dataset.slugifyObserver = '1';
  }
}

function scanAndAttach(root: ParentNode = document) {
  const nodes = Array.from(root.querySelectorAll<HTMLInputElement>('input[data-slugify]'));
  nodes.forEach(n => attachToInput(n));
}

// Start on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scanAndAttach(document));
} else {
  scanAndAttach(document);
}
